import { useState } from 'react';
import { ArrowLeft, Eye, MessageCircle, Lock, Pin, Trash2, LockOpen, Shield, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ForumReplyForm } from './ForumReplyForm';
import { ForumAvatar } from './ForumAvatar';
import { ForumUserProfileModal } from './ForumUserProfileModal';
import { useAuth } from '@/hooks/useAuth';
import { useDeleteTopic, useDeleteReply, useToggleLockTopic, useTogglePinTopic, useSoftDeleteTopic, useSoftDeleteReply } from '@/hooks/useForum';
import { toast } from 'sonner';

interface Topic {
  id: number;
  category_name: string;
  title: string;
  content: string;
  author_id: string | null;
  author_name: string;
  author_roles?: string | null;
  author_avatar_url?: string | null;
  author_default_avatar?: number | null;
  view_count: number;
  reply_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  is_deleted: boolean;
  created_at: string;
}

interface Reply {
  id: number;
  content: string;
  author_id: string | null;
  author_name: string;
  author_roles?: string | null;
  author_avatar_url?: string | null;
  author_default_avatar?: number | null;
  is_deleted: boolean;
  created_at: string;
}

function AuthorRoleBadge({ roles }: { roles?: string | null }) {
  if (!roles) return null;
  const roleList = roles.split(',');
  
  if (roleList.includes('admin')) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary">
        <Shield className="h-2.5 w-2.5" />
        Admin
      </span>
    );
  }
  if (roleList.includes('moderator')) {
    return (
      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
        <Shield className="h-2.5 w-2.5" />
        Mod
      </span>
    );
  }
  return null;
}

interface ForumTopicDetailProps {
  topic: Topic;
  replies: Reply[];
  onBack: () => void;
  onReplySuccess: () => void;
}

export function ForumTopicDetail({ topic, replies, onBack, onReplySuccess }: ForumTopicDetailProps) {
  const { canModerate, session } = useAuth();
  const deleteTopic = useDeleteTopic();
  const deleteReply = useDeleteReply();
  const softDeleteTopic = useSoftDeleteTopic();
  const softDeleteReply = useSoftDeleteReply();
  const toggleLock = useToggleLockTopic();
  const togglePin = useTogglePinTopic();
  const [deletingReplyId, setDeletingReplyId] = useState<number | null>(null);
  const [showDeleteTopicDialog, setShowDeleteTopicDialog] = useState(false);
  const [showModeratorDeleteDialog, setShowModeratorDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string | null; name: string } | null>(null);

  const handleUserClick = (userId: string | null, userName: string) => {
    if (userId) {
      setSelectedUser({ id: userId, name: userName });
    }
  };

  const currentUserId = session?.user?.id;
  const isTopicAuthor = currentUserId && topic.author_id === currentUserId;
  const hasReplies = replies.length > 0;

  const handleDeleteTopic = async () => {
    try {
      await deleteTopic.mutateAsync(topic.id);
      toast.success('Tråden har raderats');
      onBack();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte radera tråden');
    }
  };

  const handleSoftDeleteTopic = async () => {
    try {
      await softDeleteTopic.mutateAsync(topic.id);
      toast.success('Inlägget har tagits bort');
      onReplySuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort inlägget');
    }
  };

  const handleDeleteReply = async (replyId: number) => {
    try {
      await deleteReply.mutateAsync({ replyId, topicId: topic.id });
      toast.success('Svaret har raderats');
      setDeletingReplyId(null);
      onReplySuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte radera svaret');
    }
  };

  const handleSoftDeleteReply = async (replyId: number) => {
    try {
      await softDeleteReply.mutateAsync({ replyId, topicId: topic.id });
      toast.success('Svaret har tagits bort');
      setDeletingReplyId(null);
      onReplySuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ta bort svaret');
    }
  };

  const handleToggleLock = async () => {
    try {
      await toggleLock.mutateAsync(topic.id);
      toast.success(topic.is_locked ? 'Tråden är nu upplåst' : 'Tråden är nu låst');
      onReplySuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ändra låsstatus');
    }
  };

  const handleTogglePin = async () => {
    try {
      await togglePin.mutateAsync(topic.id);
      toast.success(topic.is_pinned ? 'Tråden är inte längre fäst' : 'Tråden är nu fäst');
      onReplySuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ändra fäststatus');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onBack} 
          className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Button>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
          <Eye className="h-3.5 w-3.5" />
          {topic.view_count}
          <MessageCircle className="h-3.5 w-3.5 ml-2" />
          {topic.reply_count}
        </div>
      </div>

      {/* Topic */}
      <Card className="p-5">
        <div className="space-y-3">
          {/* Title and badges */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                {Boolean(topic.is_pinned) && (
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <Pin className="h-3 w-3" />
                    Fäst
                  </Badge>
                )}
                {Boolean(topic.is_locked) && (
                  <Badge variant="secondary" className="gap-1 text-xs">
                    <Lock className="h-3 w-3" />
                    Låst
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">{topic.category_name}</Badge>
              </div>
              <h1 className="font-display text-xl font-semibold text-foreground">{topic.title}</h1>
            </div>

            {/* Actions dropdown */}
            {(isTopicAuthor || canModerate) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  {canModerate && (
                    <>
                      <DropdownMenuItem onClick={handleTogglePin}>
                        <Pin className="h-4 w-4 mr-2" />
                        {topic.is_pinned ? 'Ta bort fäst' : 'Fäst'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleToggleLock}>
                        {topic.is_locked ? (
                          <>
                            <LockOpen className="h-4 w-4 mr-2" />
                            Lås upp
                          </>
                        ) : (
                          <>
                            <Lock className="h-4 w-4 mr-2" />
                            Lås
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {isTopicAuthor && !topic.is_deleted && (
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteTopicDialog(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Ta bort
                    </DropdownMenuItem>
                  )}
                  {canModerate && (
                    <DropdownMenuItem 
                      onClick={() => setShowModeratorDeleteDialog(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Radera permanent
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Author info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors"
              onClick={() => handleUserClick(topic.author_id, topic.author_name)}
            >
              <ForumAvatar 
                avatarUrl={topic.author_avatar_url} 
                defaultAvatar={topic.author_default_avatar} 
                name={topic.author_name}
                size="md"
              />
              <span className="font-medium text-foreground hover:underline">{topic.author_name}</span>
            </div>
            <AuthorRoleBadge roles={topic.author_roles} />
            <span className="text-border">•</span>
            <span>
              {formatDistanceToNow(new Date(topic.created_at.replace('Z', '+01:00')), { 
                addSuffix: true, 
                locale: sv 
              })}
            </span>
          </div>

          {/* Content */}
          <p className={`whitespace-pre-wrap leading-relaxed ${topic.is_deleted ? 'text-muted-foreground italic' : 'text-foreground'}`}>
            {topic.content}
          </p>
        </div>
      </Card>

      {/* Replies */}
      {replies.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground px-1">
            {replies.length} {replies.length === 1 ? 'svar' : 'svar'}
          </h2>
          <div className="space-y-2">
            {replies.map((reply) => {
              const isReplyAuthor = currentUserId && reply.author_id === currentUserId;
              const canDeleteReply = canModerate || (isReplyAuthor && !reply.is_deleted);
              
              return (
                <Card key={reply.id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div 
                      className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      onClick={() => handleUserClick(reply.author_id, reply.author_name)}
                    >
                      <ForumAvatar 
                        avatarUrl={reply.author_avatar_url} 
                        defaultAvatar={reply.author_default_avatar} 
                        name={reply.author_name}
                      />
                      <span className="font-medium text-foreground hover:underline">{reply.author_name}</span>
                    </div>
                    <AuthorRoleBadge roles={reply.author_roles} />
                    <span className="text-border">•</span>
                    <span className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(reply.created_at.replace('Z', '+01:00')), { 
                        addSuffix: true, 
                        locale: sv 
                      })}
                    </span>
                    {canDeleteReply && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeletingReplyId(reply.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <p className={`whitespace-pre-wrap leading-relaxed ${reply.is_deleted ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                    {reply.content}
                  </p>

                  {/* Delete reply dialog */}
                  <AlertDialog open={deletingReplyId === reply.id} onOpenChange={(open) => !open && setDeletingReplyId(null)}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{canModerate ? 'Radera svar?' : 'Ta bort svar?'}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {canModerate 
                            ? 'Detta kommer permanent radera svaret. Åtgärden kan inte ångras.'
                            : 'Svaret markeras som borttaget och texten ersätts.'}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => canModerate ? handleDeleteReply(reply.id) : handleSoftDeleteReply(reply.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {canModerate ? 'Radera' : 'Ta bort'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Reply form */}
      {!Boolean(topic.is_locked) && (
        <ForumReplyForm topicId={topic.id} onSuccess={onReplySuccess} />
      )}

      {Boolean(topic.is_locked) && (
        <div className="text-center py-6 text-muted-foreground">
          <Lock className="h-5 w-5 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Denna tråd är låst och accepterar inga nya svar.</p>
        </div>
      )}

      {/* Delete topic dialogs */}
      <AlertDialog open={showDeleteTopicDialog} onOpenChange={setShowDeleteTopicDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort inlägg?</AlertDialogTitle>
            <AlertDialogDescription>
              {hasReplies 
                ? 'Tråden har svar och kommer därför ligga kvar, men ditt inlägg markeras som borttaget.'
                : 'Detta kommer permanent radera tråden. Åtgärden kan inte ångras.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction 
              onClick={hasReplies ? handleSoftDeleteTopic : handleDeleteTopic}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showModeratorDeleteDialog} onOpenChange={setShowModeratorDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Radera tråd permanent?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta kommer permanent radera tråden och alla svar. Åtgärden kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTopic}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Radera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ForumUserProfileModal
        userId={selectedUser?.id || null}
        userName={selectedUser?.name || ''}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}